import { useCallback, useEffect, useMemo, useState, useRef, use } from "react";
import useWebSocket from "../hooks/useWebSocket";
import useAudio from "../hooks/useAudio";
import { set } from "lodash";
import {
  registerValidationHandler,
  unregisterValidationHandler,
  registerControlSender,
  unregisterControlSender,
} from "./validationChannel";

export default function VoiceAgent({
  sessionId,
  // initialMode = "voice",
  initialMode = "text",
  agentName = "Ana",
  wsPath = "/v1/crai-poc/ws",
  onDisconnect,
  setIsOnline = () => {},
  isOnline = false,
  setIsMuted = () => {},
}) {
  const [mode, setMode] = useState(initialMode);
  const [pendingMode, setPendingMode] = useState(null);
  const [messages, setMessages] = useState([]);
  const [textInput, setTextInput] = useState("");
  const messagesEndRef = useRef(null);
  const textInputRef = useRef(null);

  const wsHandlers = useMemo(() => ({ onAudio: null, onControl: null }), []);

  const { status, sendAudio, sendControl, sendText, switchMode, close } =
    useWebSocket(sessionId, wsHandlers, { wsPath });

  const isActive = status === "connected";
  const isVoice = mode === "voice";

  // Kill audio immediately when user requests switch away from voice
  const shouldRunAudio = isVoice && pendingMode !== "text";

  const onCapturedAudio = useCallback(
    (pcmBuffer) => {
      if (shouldRunAudio) sendAudio(pcmBuffer);
    },
    [sendAudio, shouldRunAudio],
  );

  const { muted, toggleMute, playAudio, clearPlayback } = useAudio({
    active: isActive && shouldRunAudio,
    onCapturedAudio,
  });

  // Track a single pending AI validation request so that
  // QuestionnaireForm can await a validation_result message
  // coming back over this WebSocket.
  const pendingValidationRef = useRef(null);

  useEffect(() => {
    setIsMuted(muted);
  }, [muted, setIsMuted]);

  wsHandlers.onAudio = useCallback(
    (buf) => {
      if (shouldRunAudio) playAudio(buf);
    },
    [playAudio, shouldRunAudio],
  );

  // Fallback: force mode if server never sends mode_change_ack
  useEffect(() => {
    if (!pendingMode) return;
    const timer = setTimeout(() => {
      console.warn(
        `[VoiceAgent] mode_change_ack not received after 5s, forcing mode to "${pendingMode}"`,
      );
      setMode(pendingMode);
      setPendingMode(null);
    }, 5000);
    return () => clearTimeout(timer);
  }, [pendingMode]);

  // Auto-scroll chat
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  // Focus text input when switching to text mode
  useEffect(() => {
    if (!isVoice && status === "connected") {
      textInputRef.current?.focus();
    }
  }, [isVoice, status]);

  wsHandlers.onControl = useCallback(
    (msg) => {
      if (msg.event === "clear") {
        clearPlayback();
      } else if (msg.event === "end") {
        close();
        onDisconnect();
      } else if (
        msg.type === "mode_change_ack" ||
        msg.name === "mode_change_ack"
      ) {
        setMode(msg.mode);
        setPendingMode(null);
      } else if (msg.name === "ai_response" && msg.text) {
        // Text mode primary response — append or create new bubble
        setMessages((prev) => {
          const last = prev[prev.length - 1];
          if (last && last.role === "assistant" && last.streaming) {
            const updated = [...prev];
            updated[updated.length - 1] = {
              ...last,
              text: last.text + msg.text,
              streaming: false,
            };
            return updated;
          }
          return [...prev, { role: "assistant", text: msg.text }];
        });
      } else if (msg.type === "transcript") {
        if (msg.started) {
          // Streaming: new assistant bubble
          setMessages((prev) => [
            ...prev,
            { role: "assistant", text: "", streaming: true },
          ]);
        } else if (msg.delta && msg.text) {
          // Streaming: append chunk
          setMessages((prev) => {
            const last = prev[prev.length - 1];
            if (last && last.role === "assistant" && last.streaming) {
              const updated = [...prev];
              updated[updated.length - 1] = {
                ...last,
                text: last.text + msg.text,
              };
              return updated;
            }
            return [
              ...prev,
              { role: "assistant", text: msg.text, streaming: true },
            ];
          });
        } else if (msg.done) {
          // Streaming: finalize
          setMessages((prev) => {
            const last = prev[prev.length - 1];
            if (last && last.role === "assistant" && last.streaming) {
              const updated = [...prev];
              updated[updated.length - 1] = { ...last, streaming: false };
              return updated;
            }
            return prev;
          });
        } else if (msg.role === "user" && msg.text) {
          // STT user transcript
          setMessages((prev) => [...prev, { role: "user", text: msg.text }]);
        } else if (msg.role === "assistant" && msg.text) {
          // Simple (non-streaming) assistant transcript
          setMessages((prev) => {
            const last = prev[prev.length - 1];
            if (last && last.role === "assistant" && last.streaming) {
              const updated = [...prev];
              updated[updated.length - 1] = {
                ...last,
                text: last.text + msg.text,
                streaming: false,
              };
              return updated;
            }
            return [...prev, { role: "assistant", text: msg.text }];
          });
        }
      } else if (msg.type === "validation_result") {
        // Resolve any external waiter (e.g. QuestionnaireForm)
        // that requested validation for a subsection.
        if (pendingValidationRef.current?.resolve) {
          pendingValidationRef.current.resolve(msg);
          pendingValidationRef.current = null;
        }

        const text = msg.speech_text || msg.error;
        setMessages((prev) => [...prev, { role: "assistant", text }]);
      } else {
        console.debug("[VoiceAgent] unhandled control message:", msg);
      }
    },
    [clearPlayback, close, onDisconnect],
  );

  // Expose an external validation handler so that other
  // components (like QuestionnaireForm) can send structured
  // questions to this VoiceAgent instance and await the
  // corresponding validation_result.
  const externalValidationHandler = useCallback(
    (payload) => {
      if (status !== "connected") {
        return Promise.reject(new Error("VoiceAgent is not connected"));
      }

      return new Promise((resolve, reject) => {
        // If there was a stale pending request, reject it.
        if (pendingValidationRef.current?.reject) {
          pendingValidationRef.current.reject(
            new Error("Superseded by a new validation request"),
          );
        }

        pendingValidationRef.current = { resolve, reject };

        sendControl({
          type: "object",
          payload,
        });
      });
    },
    [sendControl, status],
  );

  // Allow the form to send additional control messages (such as
  // section_change) through this VoiceAgent's WebSocket when
  // it is connected.
  const formControlSender = useCallback(
    (obj) => {
      if (status !== "connected") return;
      sendControl(obj);
    },
    [sendControl, status],
  );

  useEffect(() => {
    registerValidationHandler(externalValidationHandler);
    registerControlSender(formControlSender);
    return () => {
      unregisterValidationHandler(externalValidationHandler);
      unregisterControlSender(formControlSender);

      // If something was waiting when this agent unmounted,
      // reject the promise so callers are not left hanging.
      if (pendingValidationRef.current?.reject) {
        pendingValidationRef.current.reject(
          new Error("VoiceAgent unmounted while waiting for validation"),
        );
        pendingValidationRef.current = null;
      }
    };
  }, [externalValidationHandler, formControlSender]);

  function handleEndCall() {
    sendControl({ event: "end" });
    close();
    setIsOnline(false);
    onDisconnect();
  }

  function handleSendText(e) {
    e.preventDefault();
    const text = textInput.trim();
    if (!text) return;
    setMessages((prev) => [...prev, { role: "user", text }]);
    sendText(text);
    setTextInput("");
  }

  function handleModeToggle() {
    const newMode = isVoice ? "text" : "voice";
    setPendingMode(newMode);
    switchMode(newMode);
  }

  function handleSendSampleObject() {
    if (status !== "connected") return;

    const questions = [
      {
        linkId: "age",
        question_text: "What is your age?",
        question_type: "integer",
        user_response: 300,
      },
      {
        linkId: "weight",
        question_text: "What is your weight in kg?",
        question_type: "decimal",
        user_response: 5,
      },
      {
        linkId: "height",
        question_text: "What is your height in cm?",
        question_type: "decimal",
        user_response: 500,
      },
    ];

    setMessages((prev) => [
      ...prev,
      {
        role: "user",
        text: `Validating ${questions.length} answers: ${questions.map((q) => `${q.question_text} → ${q.user_response}`).join(", ")}`,
      },
    ]);

    sendControl({
      type: "object",
      payload: {
        questions,
        context: {},
      },
    });
  }

  const agentInitials = agentName
    .split(" ")
    .map((w) => w[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);

  useEffect(() => {
    setIsOnline(isActive);
  }, [isActive, setIsOnline]);

  return (
    <div
      className="voice-agent"
      // style={{border:"1px solid red"}}
    >
      {/* Header */}
      {/* <div className="agent-header" style={{border:"1px solid blue"}}>
        <div className="agent-avatar">{agentInitials}</div>
        <div className="agent-info">
          <span className="agent-name">{agentName}</span>
          <span className={`mode-badge ${mode}`}>
            {pendingMode ? `Switching to ${pendingMode}\u2026` : isVoice ? "Voice" : "Text"}
          </span>
        </div>
        <div className={`conn-dot ${status}`} title={status} />
      </div> */}

      {/* Chat / transcript area */}
      {/* <div className="chat-area" style={{border:"1px solid green"}}>
        <div className="messages" style={{border:"1px solid purple"}}>
          {messages.length === 0 && (
            <p className="empty-state">
              {isVoice ? "Speak to get started" : "Type a message to get started"}
            </p>
          )}
          {messages.map((msg, i) => (
            <div key={i} className={`message ${msg.role}${msg.streaming ? " streaming" : ""}`}>
              {msg.role === "assistant" && (
                <div className="message-avatar">{agentInitials}</div>
              )}
              <div className="message-bubble">
                <p className="message-text">{msg.text || "\u00A0"}</p>
              </div>
            </div>
          ))}
          <div ref={messagesEndRef} />
        </div>

        {!isVoice && (
          <form className="text-input-form" onSubmit={handleSendText}>
            <input
              ref={textInputRef}
              type="text"
              value={textInput}
              onChange={(e) => setTextInput(e.target.value)}
              placeholder="Type a message\u2026"
              disabled={status !== "connected" || pendingMode !== null}
            />
            <button type="submit" disabled={status !== "connected" || pendingMode !== null || !textInput.trim()} aria-label="Send">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z" />
              </svg>
            </button>
          </form>
        )}
      </div> */}

      {/* Voice mode visualizer */}
      {/* {isVoice && status === "connected" && (
        <div className="voice-indicator">
          <div className={`mic-pulse${muted ? " muted" : ""}`}>
            <div className="pulse-ring" />
            <div className="pulse-ring delay" />
            <div className="mic-icon">
              {muted ? (
                <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M19 11h-1.7c0 .74-.16 1.43-.43 2.05l1.23 1.23c.56-.98.9-2.09.9-3.28zm-4.02.17c0-.06.02-.11.02-.17V5c0-1.66-1.34-3-3-3S9 3.34 9 5v.18l5.98 5.99zM4.27 3L3 4.27l6.01 6.01V11c0 1.66 1.33 3 2.99 3 .22 0 .44-.03.65-.08l1.66 1.66c-.71.33-1.5.52-2.31.52-2.76 0-5.3-2.1-5.3-5.1H5c0 3.41 2.72 6.23 6 6.72V21h2v-3.28c.91-.13 1.77-.45 2.54-.9L19.73 21 21 19.73 4.27 3z" />
                </svg>
              ) : (
                <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 14c1.66 0 2.99-1.34 2.99-3L15 5c0-1.66-1.34-3-3-3S9 3.34 9 5v6c0 1.66 1.34 3 3 3zm5.3-3c0 3-2.54 5.1-5.3 5.1S6.7 14 6.7 11H5c0 3.41 2.72 6.23 6 6.72V21h2v-3.28c3.28-.48 6-3.3 6-6.72h-1.7z" />
                </svg>
              )}
            </div>
          </div>
          <p className="voice-status-text">{muted ? "Microphone muted" : "Listening\u2026"}</p>
        </div>
      )} */}

      {/* Controls */}
      <div className="controls d-flex flex-column align-items-center">
        {isVoice && (
          <button
            type="button"
            className={`btn-mute${muted ? " muted" : ""} w-100`}
            onClick={toggleMute}
            disabled={status !== "connected"}
          >
            {muted ? "Unmute Mic" : "Mute Mic"}
          </button>
        )}

        {/* <button
          className="btn-mode"
          onClick={handleModeToggle}
          disabled={status !== "connected" || pendingMode !== null}
        >
          {pendingMode
            ? "Switching\u2026"
            : isVoice
              ? "Switch to Text"
              : "Switch to Voice"}
        </button> */}

        <button type="button" className="btn-end" onClick={handleEndCall}>
          {status === "disconnected" ? "Back to Home" : "End Session"}
        </button>
        {/* <button
          className="btn-send-object"
          type="button"
          onClick={handleSendSampleObject}
          disabled={status !== "connected"}
        >
          Send Object
        </button> */}
      </div>
    </div>
  );
}
